import { WestpayForm } from "./integrations/WestpayForm";
import { VivaForm } from "./integrations/VivaForm";
import { SoftpayForm } from "./integrations/SoftpayForm";
import { FortnoxForm } from "./integrations/FortnoxForm";
import { SpirisForm } from "./integrations/SpirisForm";
import { NetsForm } from "./integrations/NetsForm";
import { MarketpayForm } from "./integrations/MarketpayForm";
import { AcquirerRequestForm } from "./integrations/AcquirerRequestForm";
import { ShopifyForm } from "./integrations/ShopifyForm";
import { NameOnlyForm } from "./integrations/NameOnlyForm";
import { PersonalkollenForm } from "./integrations/PersonalkollenForm";
import type { RestIntegration } from "@KassaroAB/shared/types/integrations/integration";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import type { ChainId, StoreId } from "@KassaroAB/shared/types/ids";

interface IntegrationFormProps {
  storeId: StoreId;
  chainId?: ChainId;
  integration?: RestIntegration;
  integrationType?: string;
  onSuccess: () => void;
  onError: () => void;
  onCancel: () => void;
}

export const IntegrationForm = ({
  storeId,
  chainId,
  integration,
  integrationType,
  onSuccess,
  onError,
  onCancel,
}: IntegrationFormProps) => {
  if (integration) {
    const type = integration.type;
    if (type === IntegrationType.WESTPAY_CLOUD) {
      return (
        <WestpayForm
          storeId={storeId}
          integration={integration}
          onSuccess={onSuccess}
          onError={onError}
          onCancel={onCancel}
        />
      );
    }
    if (type === IntegrationType.VIVA) {
      return (
        <VivaForm
          storeId={storeId}
          chainId={chainId}
          integration={integration}
          onSuccess={onSuccess}
          onError={onError}
          onCancel={onCancel}
        />
      );
    }
    if (type === IntegrationType.SOFTPAY) {
      return (
        <SoftpayForm
          storeId={storeId}
          integration={integration}
          onSuccess={onSuccess}
          onError={onError}
          onCancel={onCancel}
        />
      );
    }
    if (type === IntegrationType.NETS) {
      return (
        <NetsForm
          storeId={storeId}
          integration={integration}
          onSuccess={onSuccess}
          onError={onError}
          onCancel={onCancel}
        />
      );
    }
    if (type === IntegrationType.MARKETPAY) {
      return (
        <MarketpayForm
          storeId={storeId}
          integration={integration}
          onSuccess={onSuccess}
          onError={onError}
          onCancel={onCancel}
        />
      );
    }
    if (
      type === IntegrationType.ELAVON ||
      type === IntegrationType.PAYTRIM
    ) {
      return (
        <AcquirerRequestForm
          storeId={storeId}
          integrationType={type}
          integration={integration}
          onSuccess={onSuccess}
          onError={onError}
          onCancel={onCancel}
        />
      );
    }
    if (type === IntegrationType.FORTNOX) {
      return (
        <FortnoxForm
          storeId={storeId}
          integration={integration}
          onCancel={onCancel}
        />
      );
    }
    if (type === IntegrationType.SPIRIS) {
      return (
        <SpirisForm
          storeId={storeId}
          integration={integration}
          onCancel={onCancel}
        />
      );
    }
    if (type === IntegrationType.PERSONALKOLLEN) {
      return (
        <PersonalkollenForm
          storeId={storeId}
          integration={integration}
          onSuccess={onSuccess}
          onError={onError}
          onCancel={onCancel}
        />
      );
    }
    if (type === IntegrationType.SHOPIFY && chainId) {
      return (
        <ShopifyForm
          chainId={chainId}
          integration={integration}
          onCancel={onCancel}
        />
      );
    }
    return (
      <NameOnlyForm
        chainId={chainId}
        integration={integration}
        onSuccess={onSuccess}
        onError={onError}
        onCancel={onCancel}
      />
    );
  }

  if (!integrationType) {
    return null;
  }

  if (integrationType === IntegrationType.WESTPAY_CLOUD) {
    return (
      <WestpayForm storeId={storeId} onSuccess={onSuccess} onError={onError} onCancel={onCancel} />
    );
  }

  if (integrationType === IntegrationType.VIVA) {
    return (
      <VivaForm
        storeId={storeId}
        chainId={chainId}
        onSuccess={onSuccess}
        onError={onError}
        onCancel={onCancel}
      />
    );
  }

  if (integrationType === IntegrationType.SOFTPAY) {
    return (
      <SoftpayForm storeId={storeId} onSuccess={onSuccess} onError={onError} onCancel={onCancel} />
    );
  }

  if (integrationType === IntegrationType.NETS) {
    return (
      <NetsForm storeId={storeId} onSuccess={onSuccess} onError={onError} onCancel={onCancel} />
    );
  }

  if (integrationType === IntegrationType.MARKETPAY) {
    return (
      <MarketpayForm storeId={storeId} onSuccess={onSuccess} onError={onError} onCancel={onCancel} />
    );
  }

  if (
    integrationType === IntegrationType.ELAVON ||
    integrationType === IntegrationType.PAYTRIM
  ) {
    return (
      <AcquirerRequestForm
        storeId={storeId}
        integrationType={integrationType}
        onSuccess={onSuccess}
        onError={onError}
        onCancel={onCancel}
      />
    );
  }

  if (integrationType === IntegrationType.FORTNOX) {
    return <FortnoxForm storeId={storeId} onCancel={onCancel} />;
  }

  if (integrationType === IntegrationType.SPIRIS) {
    return <SpirisForm storeId={storeId} onCancel={onCancel} />;
  }

  if (integrationType === IntegrationType.SHOPIFY && chainId) {
    return <ShopifyForm chainId={chainId} onCancel={onCancel} />;
  }

  if (integrationType === IntegrationType.PERSONALKOLLEN) {
    return (
      <PersonalkollenForm
        storeId={storeId}
        onSuccess={onSuccess}
        onError={onError}
        onCancel={onCancel}
      />
    );
  }

  return null;
};
